import { useEffect, useState } from "react";

import {
  approveOnboardingRequest,
  createWarehouse,
  fetchPendingOnboardingRequests,
  fetchWarehouses,
  rejectOnboardingRequest,
  type OnboardingRequest,
  type Warehouse,
} from "./nodeownerApiClient";

/**
 * Real data from `nodeowner-api` now (`GET/POST /wh`, `GET /inbox/pending`,
 * `POST /inbox/{uid}/approve|reject`) -- the dashboard shell's own
 * "появится после nodeowner-api" placeholder is gone now that it exists.
 */
export function Dashboard() {
  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null);
  const [requests, setRequests] = useState<OnboardingRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showAddWh, setShowAddWh] = useState(false);
  const [whName, setWhName] = useState("");
  const [whAddress, setWhAddress] = useState("");
  const [whTimeZone, setWhTimeZone] = useState("Europe/Moscow");
  const [addWhLoading, setAddWhLoading] = useState(false);

  const [actioningRequestUid, setActioningRequestUid] = useState<string | null>(null);

  function reload() {
    fetchWarehouses().then(setWarehouses).catch((e) => setError(e instanceof Error ? e.message : String(e)));
    fetchPendingOnboardingRequests()
      .then(setRequests)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(reload, []);

  async function handleAddWh() {
    setAddWhLoading(true);
    setError(null);
    try {
      await createWarehouse(whName, whAddress, whTimeZone);
      setWhName("");
      setWhAddress("");
      setShowAddWh(false);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось создать склад");
    } finally {
      setAddWhLoading(false);
    }
  }

  async function handleApprove(requestUid: string) {
    setActioningRequestUid(requestUid);
    setError(null);
    try {
      await approveOnboardingRequest(requestUid);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось одобрить заявку");
    } finally {
      setActioningRequestUid(null);
    }
  }

  async function handleReject(requestUid: string) {
    // A short blocking prompt is enough here -- this screen has no modal
    // system yet, and rejection is rare enough not to warrant building one
    // just for a single reason field.
    const reason = window.prompt("Причина отказа:");
    if (!reason) return;
    setActioningRequestUid(requestUid);
    setError(null);
    try {
      await rejectOnboardingRequest(requestUid, reason);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось отклонить заявку");
    } finally {
      setActioningRequestUid(null);
    }
  }

  return (
    <div className="page-content">
      {error && <p className="error-text" role="alert">{error}</p>}

      <div className="table-toolbar">
        <span className="table-toolbar-title">Склады</span>
        <button className="btn btn-secondary" onClick={() => setShowAddWh((s) => !s)}>
          {showAddWh ? "Отмена" : "+ Склад"}
        </button>
      </div>

      {showAddWh && (
        <div className="card" style={{ marginBottom: "24px" }}>
          <div className="form-row">
            <div className="field">
              <label className="field-label" htmlFor="wh-name">Название</label>
              <input id="wh-name" className="input" value={whName} onChange={(e) => setWhName(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="wh-address">Адрес</label>
              <input id="wh-address" className="input" value={whAddress} onChange={(e) => setWhAddress(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="wh-tz">Часовой пояс</label>
              <input id="wh-tz" className="input" value={whTimeZone} onChange={(e) => setWhTimeZone(e.target.value)} />
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleAddWh}
            disabled={addWhLoading || !whName || !whAddress || !whTimeZone}
          >
            {addWhLoading ? "Создаём…" : "Создать"}
          </button>
        </div>
      )}

      {warehouses === null ? (
        <p className="status-text">Загружаем…</p>
      ) : warehouses.length === 0 ? (
        <p className="status-text">Пока нет ни одного склада</p>
      ) : (
        <div className="data-table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Адрес</th>
                <th>Часовой пояс</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map((wh) => (
                <tr key={wh.whUid}>
                  <td className="data-table-name">{wh.nodeName}</td>
                  <td>{wh.nodeAddress}</td>
                  <td>{wh.timeZone}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <hr className="divider" />

      <div className="table-toolbar">
        <span className="table-toolbar-title">Заявки на подключение</span>
      </div>

      {requests === null ? (
        <p className="status-text">Загружаем…</p>
      ) : requests.length === 0 ? (
        <p className="status-text">Заявок пока нет</p>
      ) : (
        <div>
          {requests.map((req) => (
            <div className="list-row" key={req.requestUid}>
              <div className="list-row-main">
                <div className="list-row-text">
                  <span className="list-row-title">{req.phone}</span>
                  <span className="helper-text">{new Date(req.createdAt).toLocaleString("ru-RU")}</span>
                </div>
              </div>
              <div className="data-table-actions">
                <button
                  className="row-action-btn"
                  disabled={actioningRequestUid === req.requestUid}
                  onClick={() => handleApprove(req.requestUid)}
                >
                  Одобрить
                </button>
                <button
                  className="row-action-btn"
                  disabled={actioningRequestUid === req.requestUid}
                  onClick={() => handleReject(req.requestUid)}
                >
                  Отклонить
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
